/**
 * Third-person orbit camera with collision.
 *
 * The camera sits behind the player and pulls in when something is between the
 * two — without that, walking up to a wall puts the camera inside it and the
 * player sees the inside of the geometry.
 */

import type { Scene } from '@babylonjs/core/scene.js';
import type { UniversalCamera } from '@babylonjs/core/Cameras/universalCamera.js';
import { Vector3 } from '@babylonjs/core/Maths/math.vector.js';
import { Ray } from '@babylonjs/core/Culling/ray.js';
import '@babylonjs/core/Culling/ray.js';

/* From the shared package, not a local copy. Movement moved there in Stage 3
   so the server could run the same rules; this import was left pointing at
   the old path and only ever compiled because `import type` is erased before
   the bundler resolves anything. */
import type { PlayerState } from '@emberreach/shared';

export const MIN_PITCH = -0.35;
export const MAX_PITCH = 1.15;
export const MIN_DISTANCE = 1.6;
export const MAX_DISTANCE = 9;

export interface CameraState {
  yaw: number;
  pitch: number;
  /** What the player asked for. */
  desiredDistance: number;
  /** What collision allows right now. */
  actualDistance: number;
}

export function createCameraState(facing: number): CameraState {
  return { yaw: facing, pitch: 0.32, desiredDistance: 6.2, actualDistance: 6.2 };
}

export function clampPitch(pitch: number): number {
  return Math.max(MIN_PITCH, Math.min(MAX_PITCH, pitch));
}

export function applyLook(state: CameraState, deltaYaw: number, deltaPitch: number): void {
  state.yaw += deltaYaw;
  state.pitch = clampPitch(state.pitch + deltaPitch);
}

/**
 * Wheel zoom. `delta` is in the same units the browser reports, so one notch of
 * a typical wheel moves the camera about a metre.
 */
export function applyZoom(state: CameraState, delta: number): void {
  state.desiredDistance = Math.max(
    MIN_DISTANCE,
    Math.min(MAX_DISTANCE, state.desiredDistance + delta * 0.01)
  );
}

/** Where the camera wants to be, ignoring obstacles. */
export function desiredPosition(
  state: CameraState,
  player: PlayerState,
  targetHeight: number
): Vector3 {
  const horizontal = Math.cos(state.pitch) * state.desiredDistance;
  const vertical = Math.sin(state.pitch) * state.desiredDistance;
  return new Vector3(
    player.x - Math.sin(state.yaw) * horizontal,
    player.y + targetHeight + vertical,
    player.z - Math.cos(state.yaw) * horizontal
  );
}

/**
 * Updates the camera, pulling it in when the view is blocked.
 *
 * Snapping in is instant but easing back out is gradual: pushing the camera
 * through a wall for even one frame looks broken, whereas easing it back out
 * after passing an obstacle looks deliberate.
 */
export function updateCamera(
  camera: UniversalCamera,
  state: CameraState,
  player: PlayerState,
  scene: Scene,
  dt: number
): void {
  const targetHeight = 1.55;
  const target = new Vector3(player.x, player.y + targetHeight, player.z);
  const wanted = desiredPosition(state, player, targetHeight);

  const toCamera = wanted.subtract(target);
  const distance = toCamera.length();
  let allowed = state.desiredDistance;

  if (distance > 0.001) {
    const direction = toCamera.scale(1 / distance);
    const ray = new Ray(target, direction, state.desiredDistance + 0.4);
    /* Only buildings and terrain push the camera. Letting every mesh block it
       meant a lamp post or a market stall slammed the camera to its minimum
       distance as the player walked past, which fills the screen with the back
       of the character's head. Big things block the view; small things do not. */
    const hit = scene.pickWithRay(
      ray,
      (mesh) =>
        mesh.isPickable &&
        (mesh.metadata as { cameraBlocker?: boolean } | null)?.cameraBlocker === true
    );
    if (hit?.hit === true && hit.distance > 0) {
      // Stop short of the surface so the near plane does not clip through it.
      allowed = Math.max(MIN_DISTANCE, hit.distance - 0.35);
    }
  }

  if (allowed < state.actualDistance) {
    state.actualDistance = allowed;
  } else {
    const ease = 1 - Math.pow(0.001, dt); // frame-rate independent smoothing
    state.actualDistance += (allowed - state.actualDistance) * ease * 0.6;
  }
  state.actualDistance = Math.max(MIN_DISTANCE, Math.min(MAX_DISTANCE, state.actualDistance));

  const horizontal = Math.cos(state.pitch) * state.actualDistance;
  const vertical = Math.sin(state.pitch) * state.actualDistance;
  camera.position.set(
    player.x - Math.sin(state.yaw) * horizontal,
    player.y + targetHeight + vertical,
    player.z - Math.cos(state.yaw) * horizontal
  );
  camera.setTarget(target);
}
